/**
 * Vor-/Nachname bei Privatpersonen.
 *
 * `Customer.name` bleibt UEBERALL der Anzeige-/Vollname (PDF, Angebot,
 * Rechnung, Suche, Dublettenpruefung). firstName/lastName sind die zusaetzliche
 * Zerlegung fuer Privatpersonen: DATEV verlangt beide Teile getrennt, und die
 * Briefanrede braucht den Nachnamen allein ("Sehr geehrter Herr Dr. Doerfler").
 * Der TITEL gehoert nicht in den Namen — er hat ein eigenes Feld und stuende
 * sonst doppelt in jeder Liste.
 *
 * Pur und ohne Abhaengigkeiten. Leitregel: lieber ein unzerlegter Name als eine
 * falsche Zerlegung — jede Regel hier ist deterministisch, es wird nichts
 * geraten.
 */

export interface ParsedPersonName {
    /** "Herr"/"Frau" — "Herrn" wird auf "Herr" normalisiert. */
    salutation?: string;
    /** Akademischer Grad ("Dr.", "Prof. Dr.", "Dipl.-Ing."). */
    title?: string;
    firstName?: string;
    lastName?: string;
}

/** Anzeigename einer Person aus den Teilen — ohne Titel (siehe Modulkopf). */
export const composePersonName = (firstName?: string, lastName?: string): string =>
    [firstName, lastName]
        .map((v) => (v ?? '').trim())
        .filter(Boolean)
        .join(' ');

const SALUTATIONS: Record<string, string> = {
    herr: 'Herr',
    herrn: 'Herr',
    'hr.': 'Herr',
    frau: 'Frau',
    'fr.': 'Frau',
};

/**
 * Anrede vereinheitlichen: "Herrn" (Briefform) und "Hr." werden zu "Herr".
 * Unbekannte Werte bleiben unveraendert stehen — lieber eine ungewohnte Anrede
 * als eine verworfene.
 */
export const normalizeSalutation = (raw?: string): string | undefined => {
    const t = (raw ?? '').trim();
    if (!t) return undefined;
    return SALUTATIONS[t.toLowerCase()] ?? t;
};

/**
 * Titel-Bausteine. Geprueft wird nur am ANFANG des Namens (bzw. hinter dem
 * Komma) — ein Nachname "Mag" am Ende bleibt darum ein Nachname.
 */
const TITLE_WORDS = new Set([
    'prof', 'dr', 'dipl', 'ing', 'mag', 'med', 'dent', 'vet', 'habil',
    'jur', 'rer', 'nat', 'oec', 'pol', 'phil', 'mult', 'kfm', 'kffr', 'wirt',
    'h.c', 'hc', 'mba', 'msc', 'bsc', 'll.m', 'llm', 'em',
]);

const bareToken = (t: string): string => t.toLowerCase().replace(/\.$/, '');

/** "Dr." / "Prof." / "Dipl.-Ing." / "Dr.-Ing." — auch zusammengesetzt. */
const isTitleToken = (token: string): boolean => {
    const key = bareToken(token);
    if (!key) return false;
    if (TITLE_WORDS.has(key)) return true;
    const parts = key.split('-');
    return parts.length > 1 && parts.every((p) => TITLE_WORDS.has(bareToken(p)));
};

/**
 * Namensvorsaetze gehoeren zum NACHNAMEN ("Hans von Mueller" -> von Mueller).
 * DATEV kennt dafuer eine eigene Spalte; wir halten sie am Nachnamen, damit
 * name = Vorname + Nachname den Menschen weiterhin korrekt benennt.
 */
const NAME_PARTICLES = new Set([
    'von', 'vom', 'van', 'zu', 'zur', 'zum', 'de', 'del', 'della', 'der', 'den',
    'di', 'da', 'dos', 'du', 'la', 'le', 'ten', 'ter', 'af', 'av', 'y',
]);

/**
 * Mehr als eine Person in einem Feld ("Angela Albert und Matthias Albert",
 * "Eheleute Schmidt"). Solche Namen werden NICHT zerlegt.
 */
const MULTI_PERSON = /(^|\s)(und|u\.|sowie|eheleute|geb\.)(\s|$)|[&+]|\bgbr\b/i;

/** Fuehrende Titel-Token abtrennen; der letzte Token bleibt immer der Name. */
const takeTitle = (input: string): { title?: string; rest: string } => {
    const tokens = input.split(' ').filter(Boolean);
    const titleTokens: string[] = [];
    while (tokens.length > 1 && isTitleToken(tokens[0])) {
        titleTokens.push(tokens.shift() as string);
    }
    return { title: titleTokens.join(' ') || undefined, rest: tokens.join(' ') };
};

/** Letztes Token = Nachname, davor stehende Namensvorsaetze wandern mit. */
const splitFirstLast = (input: string): { firstName?: string; lastName?: string } => {
    const tokens = input.split(' ').filter(Boolean);
    if (!tokens.length) return {};
    if (tokens.length === 1) return { lastName: tokens[0] };
    let cut = tokens.length - 1;
    while (cut > 1 && NAME_PARTICLES.has(tokens[cut - 1].toLowerCase())) cut -= 1;
    return {
        firstName: tokens.slice(0, cut).join(' ') || undefined,
        lastName: tokens.slice(cut).join(' '),
    };
};

/**
 * Rohnamen zerlegen. Erkannte Muster (aus dem echten Steuerberater-Export):
 *
 *   "Nachname, [Titel] Vorname"        -> Barialai, Dr. Akbar
 *   "[Herr|Herrn|Frau] [Titel] Vorname [...] Nachname"
 *   "[Titel] Vorname Nachname"         -> Dr. Michael Commichau
 *
 * FALLBACK-REGEL (deterministisch, dokumentiert): laesst sich der Name nicht
 * sauber in zwei Teile trennen — Mehrpersonen-Namen wie "Angela Albert und
 * Matthias Albert", oder ein einzelnes Token — bleibt firstName LEER und der
 * unveraenderte Rohname landet in lastName. So geht kein Zeichen verloren:
 * composePersonName liefert dann exakt wieder den Rohnamen.
 */
export const parsePersonName = (raw: string): ParsedPersonName => {
    const clean = (raw ?? '').replace(/\s+/g, ' ').trim();
    if (!clean) return {};
    // Mehrere Personen in einem Feld: nichts zerlegen, nichts wegwerfen.
    if (MULTI_PERSON.test(clean)) return { lastName: clean };

    let rest = clean;
    let salutation: string | undefined;
    const sal = /^(herrn|herr|hr\.|frau|fr\.)\s+/i.exec(rest);
    if (sal) {
        salutation = SALUTATIONS[sal[1].toLowerCase()];
        rest = rest.slice(sal[0].length).trim();
    }

    const comma = rest.indexOf(',');
    if (comma > 0) {
        const left = rest.slice(0, comma).trim();
        const right = rest.slice(comma + 1).trim();
        // Titel steht bei dieser Schreibweise hinter dem Komma.
        const { title, rest: firstPart } = takeTitle(right);
        if (left) {
            return {
                salutation,
                title,
                firstName: firstPart || undefined,
                lastName: left,
            };
        }
        rest = firstPart;
    }

    const { title, rest: nameRest } = takeTitle(rest);
    if (!nameRest) return { salutation, title };
    return { salutation, title, ...splitFirstLast(nameRest) };
};
